<script lang="ts">
  import { onMount } from "svelte";
  import SearchHeader from "../../components/SearchHeader.svelte";
  import { Table } from "../../components/Table";
  import {
    filter,
    loading,
    records,
    pagination,
    initList,
    onDelete,
    onPageChange,
    search,
    record,
    save,
    initDetail,
    uploading,
    load,
    comparing,
    closeComparison,
    openComparison,
  } from "./quality-document.store";
  import { qualityDocumentFields, qualityDocumentVersionFields } from "../../utils/TableFields/quality-document.fields";
  import type { IQualityDocument } from "../../entities/quality-document";
  import FormLayout from "../../layout/FormLayout.svelte";
  import FormField from "../../components/Form/FormField.svelte";
  import Input from "../../components/Form/Fields/Input.svelte";
  import type { IApiResponse } from "../../utils/api/api-response";
  import Textarea from "../../components/Form/Fields/Textarea.svelte";
  import FileUploadButton from "../../components/FileUploadButton.svelte";
  import { StoredFileService } from "../../services/stored-file.service";
  import Label from "../../components/Form/Label.svelte";
  import { get } from "svelte/store";
  import DateInputString from "../../components/Form/Fields/DateInputString.svelte";
  import Button from "../../components/Button.svelte";
  import PdfBrowser from "../../components/PdfBrowser.svelte";

  export let entityName: string;
  export let clfDocTypeCode: string;
  export let withVersion: boolean;
  export let comparisonUrl: string = "";

  $: isNew = $record != null && $record.oid == 0;
  $: canSubmit = $record != null && $record.name != "" && $record.documentDate != "" && (!withVersion || $record.version != "") && $record.storedFileId > 0;

  onMount(() => initList(clfDocTypeCode));

  async function onSubmit() {
    let newP = isNew;
    let _resp: IApiResponse<IQualityDocument> = await save();
    onCancel();
  }

  function onCancel() {
    record.set(null);
    load();
  }

  function onCreate() {
    initDetail(0);
  }

  function onEdit(item: IQualityDocument) {
    initDetail(item.oid);
  }

  async function uploadFile(event: CustomEvent<File>) {
    uploading.set(true);
    try {
      const file: File = event.detail;
      let _res = await StoredFileService.uploadStoredFile(file);
      let fileId = _res.payload.oid;
      let _rec = get(record);
      record.set(<IQualityDocument>{ ..._rec, storedFileId: fileId });
    } finally {
      uploading.set(false);
    }
  }

  async function onDownloadFile(item: IQualityDocument) {
    StoredFileService.downloadStoredFile(item.storedFileId);
  }
</script>

{#if $record == null}
  <SearchHeader
    entitySingular={entityName}
    entityPlural={entityName}
    numRecords={$pagination != null ? $pagination.totalRecords : 0}
    bind:searchValue={$filter.criteria.query}
    on:search={search}
  ></SearchHeader>

  <Table
    loading={$loading}
    fields={withVersion ? qualityDocumentVersionFields : qualityDocumentFields}
    records={$records}
    pagination={$pagination}
    createEnabled={true}
    editEnabled={true}
    deleteEnabled={true}
    exportRecordEnabled={true}
    on:create={(_) => onCreate()}
    on:edit={(e) => onEdit(e.detail)}
    on:delete={(e) => onDelete(e.detail)}
    on:pageChange={(e) => onPageChange(e.detail)}
    on:exportRecord={(e) => onDownloadFile(e.detail)}
  />
{:else if $comparing}
  <div style="margin-top:1rem;float:right;">
    <Button on:click={closeComparison}>Chiudi</Button>
  </div>
  <div class="columns">
    <div class="column is-6">
      <PdfBrowser url={StoredFileService.getStoredFileUrl($record.storedFileId)} />
    </div>
    <div class="column is-6">
      <PdfBrowser url={comparisonUrl} />
    </div>
  </div>
{:else}
  <div class="columns" style="margin-top:1rem;">
    <div class="column is-12">
      <FormLayout {canSubmit} {isNew} on:submit={onSubmit} on:cancel={onCancel}>
        <div class="columns">
          <div class="column is-4">
            <FormField>
              <Input name="Nome" placeholder="" required={true} bind:value={$record.name} />
            </FormField>
          </div>
          {#if withVersion}
            <div class="column is-4">
              <FormField>
                <Input name="Versione" placeholder="" required={true} bind:value={$record.version} />
              </FormField>
            </div>
          {/if}
          {#if isNew}
            <div class="column is-4">
              <FormField>
                <Label>&nbsp;</Label>
                <div>
                  <FileUploadButton text="Carica file..." disabled={$uploading} on:fileUpload={uploadFile} />
                  {#if $uploading}
                    <i class="material-icons">hourglass_bottom</i>
                  {:else if $record.storedFileId > 0}
                    <i class="material-icons">check</i>
                  {:else}
                    <i class="material-icons">close</i>
                  {/if}
                </div>
              </FormField>
            </div>
          {:else if !!comparisonUrl}
            <div class="column is-4">
              <FormField>
                <Label>&nbsp;</Label>
                <div>
                  <Button klass="is-warning" on:click={openComparison}>Confronta</Button>
                </div>
              </FormField>
            </div>
          {/if}
        </div>

        <div class="columns">
          <div class="column is-4">
            <FormField>
              <DateInputString name="Data Documento" placeholder="" required={true} bind:value={$record.documentDate} />
            </FormField>
          </div>
        </div>

        <div class="columns">
          <div class="column is-12">
            <FormField>
              <Textarea name="Note" placeholder="note" required={false} bind:value={$record.notes} />
            </FormField>
          </div>
        </div>
      </FormLayout>
    </div>
  </div>
{/if}
